import async from 'async';
import {
  assign,
  chain,
  every,
  get,
  map,
  mapValues,
  some,
  startCase,
} from 'lodash';
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import {
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  List,
  ListItem,
  Typography,
} from '@mui/material';
import DoneIcon from '@mui/icons-material/CheckCircle';
import ClearIcon from '@mui/icons-material/Clear';

import { makeStyles } from 'tss-react/mui';
import CountryList from '../../../../Lib/Constants/countries';
import IBANCountries from '../../../../Lib/Constants/iban-countries';
import isDomesticUtil from '../../../../Lib/Utils/VendorApplication/is-domestic';
import isNumeric from '../../../../Lib/Utils/Numeric/isNumeric';

const useStyles = makeStyles()((theme) => ({
  actions: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'flex-end',
  },
  checks: {
    display: 'flex',
    flex: '1',
    flexDirection: 'column',
    justifyContent: 'space-between',
  },
  dialog: {},
  dialogContent: {
    display: 'flex',
    flexDirection: 'column',
    height: '300px',
    justifyContent: 'space-between',
    width: '300px',
  },
  errors: {
    color: get(theme, 'palette.destructive.main'),
    fontSize: '11px',
  },
  list: {
    padding: '0px',
  },
  listIconDone: {
    fill: get(theme, 'palette.primary.main'),
  },
  listIconPending: {
    height: '24px !important',
    width: '24px !important',
  },
  listIconError: {
    fill: get(theme, 'palette.destructive.main'),
  },
  listItem: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'space-between',
    padding: '8px 0px',
  },
  listSpan: {
    textTransform: 'uppercase',
  },
}));

const validatePhone = (number) => {
  if (isNumeric(number) && number.length > 6) {
    return Promise.resolve({ number, isValid: true });
  }

  return Promise.reject(
    new Error('The phone number must have at least 7 digits and be numeric.'),
  );
};

const isIBANCountry = (country) => {
  const name = chain(CountryList)
    .find((v) => v.value === country)
    .get('text')
    .value();

  return !!get(IBANCountries, name);
};

const makeInitialChecksState = (form, domestic, paymentCountry) => {
  const state = { submitted: false, phone: false };

  if (domestic) {
    assign(state, { taxId: false });
  }

  if (isDomesticUtil(paymentCountry)) {
    assign(state, { bankAccounts: false });
  } else {
    assign(state, { bic: false });
  }

  if (isIBANCountry(paymentCountry)) {
    assign(state, { iban: false });
  }

  return state;
};

export default function SubmitApplicationChecksModal(props) {
  const { classes } = useStyles();

  const isDomestic = isDomesticUtil(props.form.country);
  const isDomesticPayment = isDomesticUtil(props.form.payment_country);
  const [checks, setChecks] = useState(
    makeInitialChecksState(props.form, isDomestic, props.form.payment_country),
  );
  const [checking, setChecking] = useState(mapValues(checks, () => false));
  const [errors, setErrors] = useState(mapValues(checks, () => false));

  function onCheck(field, response = {}, cb) {
    if (response.isValid) {
      setChecks((c) => ({ ...c, [field]: response.isValid }));
    } else {
      setErrors((e) => ({
        ...e,
        [field]: `The provided ${startCase(field)} is invalid`,
      }));
    }

    setChecking((c) => ({ ...c, [field]: false }));
    cb(null, response);
  }

  function validate(method, value, field, cb) {
    method(value)
      .then((response) => onCheck(field, response, cb))
      .catch((err) => cb({ [field]: get(err, 'message') }));
  }

  function checkBankAccounts(cb) {
    setTimeout(() => {
      setChecks((c) => ({ ...c, bankAccounts: true }));
      setChecking((c) => ({ ...c, bankAccounts: false }));
      cb(null, { isValid: true });
    }, 2000);
  }

  function checkTaxId(cb) {
    let lastName = null;
    if (props.form.is_individual) {
      lastName = props.form.last_name;
    } else {
      lastName = props.form.legal_name;
    }
    const args = { tin: props.form.tax_id, lastName };

    validate(props.validateTIN, args, 'taxId', cb);
  }

  function checkIBAN(cb) {
    validate(props.validateIBAN, props.form.account_number, 'iban', cb);
  }

  function checkBIC(cb) {
    validate(props.validateBIC, props.form.routing_number, 'bic', cb);
  }

  function checkPhone(cb) {
    validate(validatePhone, props.form.phone, 'phone', cb);
  }

  function submit(results, cb) {
    if (some(results, (v) => !v.isValid)) return cb();

    if (props.beforeSign) {
      return props.submitVendor(props.user.id);
    }

    return props
      .submitVendor(props.user.id)
      .then(() => {
        setChecks((c) => ({ ...c, submitted: true }));
        cb();
      })
      .catch((err) => cb({ submitted: get(err, 'message') }));
  }

  function onCompleteCheck(err) {
    if (err) {
      setErrors((e) => ({ ...e, ...err }));
    }
    setChecking((c) => ({ ...c, ...mapValues(c, () => false) }));
  }

  function check() {
    const validators = { checkPhone };

    if (isDomestic) assign(validators, { checkTaxId });

    assign(
      validators,
      isDomesticPayment ? { checkBankAccounts } : { checkBIC },
      isIBANCountry(props.form.payment_country) ? { checkIBAN } : {},
    );

    async.auto(
      { ...validators, submit: [...Object.keys(validators), submit] },
      onCompleteCheck,
    );
  }

  useEffect(() => {
    setChecking((c) => ({ ...c, ...mapValues(c, () => true) }));
    return check();
  }, [props.open]);

  function makeIcon(field) {
    if (checks[field]) return <DoneIcon className={classes.listIconDone} />;
    if (checking[field])
      return (
        <CircularProgress
          classes={{ root: classes.listIconPending }}
          color="primary"
        />
      );
    return <ClearIcon className={classes.listIconError} />;
  }

  return (
    <Dialog open classes={{ root: classes.dialog }} onClose={props.onClose}>
      <DialogTitle>Application Checks</DialogTitle>

      <DialogContent classes={{ root: classes.dialogContent }}>
        <div className={classes.checks}>
          <List className={classes.list}>
            {map(checks, (_, k) => (
              <ListItem key={k} className={classes.listItem}>
                {makeIcon(k)}
                <Typography variant="body2" className={classes.listSpan}>
                  {startCase(k)}
                </Typography>
              </ListItem>
            ))
              .filter((c) => !(c.key === 'submitted' && props.beforeSign))
              .reverse()}
          </List>

          {every(checks) && (
            <p>
              Thank you for submitting your application, it will now be reviewed
              by compliance at fashionnova. We will let you know when you have
              been approved.
            </p>
          )}

          <div className={classes.errors}>
            {chain(errors)
              .values()
              .compact()
              .thru((e) =>
                e.length > 0
                  ? e.concat(
                      props.beforeSign
                        ? 'Please fix the errors to sign.'
                        : 'Please fix the errors and re-submit.',
                    )
                  : e,
              )
              .map((e) => <p key={e}>{e}</p>)
              .value()}
          </div>
        </div>

        <div className={classes.actions}>
          <Button
            variant="contained"
            color="primary"
            size="small"
            onClick={props.onClose}
            disabled={some(checking)}
          >
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

SubmitApplicationChecksModal.propTypes = {
  form: PropTypes.shape({
    country: PropTypes.string,
    tax_id: PropTypes.string,
    account_number: PropTypes.string,
    routing_number: PropTypes.string,
    is_individual: PropTypes.bool,
    first_name: PropTypes.string,
    last_name: PropTypes.string,
    legal_name: PropTypes.string,
    payment_country: PropTypes.string,
    phone: PropTypes.string,
  }).isRequired,
  onClose: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
  submitVendor: PropTypes.func.isRequired,
  user: PropTypes.shape({
    id: PropTypes.number,
  }).isRequired,
  validateBIC: PropTypes.func.isRequired,
  validateTIN: PropTypes.func.isRequired,
  validateIBAN: PropTypes.func.isRequired,
  beforeSign: PropTypes.bool.isRequired,
};
